/* eslint-disable linebreak-style */
/* eslint-disable @typescript-eslint/default-param-last */
import React from 'react';
import { AxiosError } from 'axios';
import { useAxios } from '../Components/Auth';
import { LLMModel } from '../Components/Contexts/LLMSettingContext';
import { useLogger } from '../Components/Logger';

export enum ChatRole {
  User = 0,
  Assistant = 1,
  System = 2,
}

export type ChatMessage = {
  Role: ChatRole,
  Content: string,
};

// eslint-disable-next-line max-len
const generalErrorMessage = "I apologize, but it seems we're experiencing a temporary glitch. Please give it another try in a moment. If the issue persists, feel free to reach out again. Thank you for your patience!";

export const useTextCompletion = () => {
  const { idTokenInstance } = useAxios();
  const logger = useLogger();
  const summarizeConversationInitialized = React.useRef(false);

  const getTextCompletion = React.useCallback(async (prompt: string, maxTokens: number, temperature: number): Promise<string | undefined> => {
    if (idTokenInstance) {
      const response = await idTokenInstance.post(`${process.env.REACT_APP_API_URL}/Internal/GetTextCompletion`, {
        prompts: prompt,
        temperature,
        maxTokens,
      });

      if (response.status === 200) {
        return response.data.text;
      }
    }
    return undefined;
  }, [idTokenInstance]);

  const getChatCompletion = React.useCallback(async (
    prompt: string,
    messages: ChatMessage[],
    maxTokens: number,
    temperature: number,
    ignoreDefaultFewShotExamples: boolean = false,
    // deprecated, use deployment instead
    model: LLMModel | null = null,
    deployment: string,
    conversationId: string,
    autoSave: boolean = true,
    stopSequences: string[] = ['|endoftext|'],
  ): Promise<string | undefined> => {
    if (idTokenInstance) {
      // Remove the general error message from the prompts
      const filteredMessages = messages.filter((msg) => !(msg.Role === ChatRole.Assistant && msg.Content === generalErrorMessage));

      const response = await idTokenInstance.post(`${process.env.REACT_APP_API_URL}/Internal/GetChatCompletion`, {
        prompt,
        temperature,
        maxTokens,
        messages: filteredMessages,
        stopSequences,
        ignoreDefaultFewShotExamples,
        // model : model === LLMModel.Companion ? LLMModel.Gpt35Turbo : model,
        deployment,
        isFineTuning: model === LLMModel.Companion,
        conversationId,
        autoSave,
      });

      if (response.status === 200) {
        // 1st round: check if the response is the same as the last message in the conversation, retry again
        if (filteredMessages.length >= 2 && response.data.text === filteredMessages[filteredMessages.length - 2].Content) {
          // eslint-disable-next-line max-len
          logger.logInfo(`Repeated response detected, retry 1st. Response Message: ${response.data.text}, request message: ${JSON.stringify(filteredMessages.filter((msg) => msg.Role !== 2))}`);
          const response1 = await idTokenInstance.post(`${process.env.REACT_APP_API_URL}/Internal/GetChatCompletion`, {
            prompt,
            temperature,
            maxTokens,
            messages: filteredMessages,
            stopSequences,
            ignoreDefaultFewShotExamples,
            // model : model === LLMModel.Companion ? LLMModel.Gpt35Turbo : model,
            deployment,
            isFineTuning: model === LLMModel.Companion,
            conversationId,
            autoSave,
          });
          if (response1.status === 200) {
            // 2nd round: check if the response is the same as the last message in the conversation, retry with new deploy name GPT4o
            if (filteredMessages.length >= 2 && response1.data.text === filteredMessages[filteredMessages.length - 2].Content) {
              // eslint-disable-next-line max-len
              logger.logInfo(`Repeated response detected, retry 2nd. Response Message: ${response1.data.text}, request message: ${JSON.stringify(filteredMessages.filter((msg) => msg.Role !== 2))}`);
              const response2 = await idTokenInstance.post(`${process.env.REACT_APP_API_URL}/Internal/GetChatCompletion`, {
                prompt,
                temperature,
                maxTokens,
                messages: filteredMessages,
                stopSequences,
                ignoreDefaultFewShotExamples,
                // model : model === LLMModel.Companion ? LLMModel.Gpt35Turbo : model,
                deployment: 'GPT4o',
                isFineTuning: model === LLMModel.Companion,
                conversationId,
                autoSave,
              });
              if (response2.status === 200) {
                // eslint-disable-next-line max-len
                logger.logInfo(`Repeated response detected, retry response from GPT4o. Response Message: ${response2.data.text}, request message: ${JSON.stringify(filteredMessages.filter((msg) => msg.Role !== 2))}`);
                return response2.data.text;
              }
              return generalErrorMessage;
            } // end of 2nd round
            return response1.data.text;
          }
          return generalErrorMessage;
        } // end of 1st round
        return response.data.text;
      }

      return generalErrorMessage;
    }
    return undefined;
  }, [idTokenInstance, logger]);

  const summarizeConversation = React.useCallback(async (conversationId: string) => {
    if (idTokenInstance) {
      try {
        const response = await
        idTokenInstance.post(`${process.env.REACT_APP_API_URL}/Internal/SummarizeConversation?currentConversationId=${conversationId}`);

        if (response.status !== 200) {
          // eslint-disable-next-line @typescript-eslint/no-throw-literal
          throw response;
        }

        logger.logInfo(`Triggered to summarize conversation with new conversationId: ${conversationId}`);
      } catch (error) {
        logger.logError(`Failue to trigger conversation summarize: ${JSON.stringify({
          conversationId,
          status: (error as AxiosError)?.response?.status,
          result: (error as AxiosError)?.response?.data,
        })}`);
      }
    }
  }, [idTokenInstance, logger]);

  const initializeSummarizeConversation = React.useCallback(async (conversationId: string) => {
    if (idTokenInstance && !summarizeConversationInitialized.current) {
      summarizeConversationInitialized.current = true;
      summarizeConversation(conversationId);
    }
  }, [idTokenInstance, logger]);

  return {
    getTextCompletion,
    getChatCompletion,
    summarizeConversation,
    initializeSummarizeConversation,
  };
};
